import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Progress, Typography, Spin, Alert, Button, Table, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
  getPanelVinculacionResumen,
  type PanelVinculacionResumen,
} from '../../api/vinculacionApi';
import { fct } from '../../fct/tokens/tokens';
import { claseFilaAtencion, FctGlifoAtencion, FctIcono, type FctNivelAtencion } from '../../fct/react';

const { Title, Text } = Typography;

// Semáforo de vinculación (convención GCvsc): >95 % normal, 80-95 % atención, <80 % urgente.
// Se dice con el glifo de atención junto a la cifra, no con el color de la cifra ni de la barra.
const semaforo = (pct: number): FctNivelAtencion =>
  pct >= 95 ? 'normal' : pct >= 80 ? 'atencion' : 'urgente';

const pct = (parte: number, total: number): number =>
  total > 0 ? Math.round((parte / total) * 1000) / 10 : 0;

const nf = new Intl.NumberFormat('es-ES');

interface DetalleFila {
  key: string;
  label: string;
  total: number;
  vinc: number;
  pend: number;
  critico?: boolean;
}

/** Subtabla de detalle dentro de cada card (Con titular / Sin titular, Titulares / Empleados, …). */
const Subtabla: React.FC<{ filas: DetalleFila[] }> = ({ filas }) => (
  <Table<DetalleFila>
    size="small"
    pagination={false}
    dataSource={filas}
    style={{ marginTop: 12 }}
    columns={[
      { title: '', dataIndex: 'label', key: 'label' },
      { title: 'Total', dataIndex: 'total', key: 'total', className: 'fct-num', align: 'right', render: (v: number) => nf.format(v) },
      { title: 'Vinc.', dataIndex: 'vinc', key: 'vinc', className: 'fct-num', align: 'right', render: (v: number) => nf.format(v) },
      {
        title: 'Pend.',
        dataIndex: 'pend',
        key: 'pend',
        className: 'fct-num',
        align: 'right',
        render: (v: number, row) => (
          <span style={row.critico && v > 0 ? { color: fct.estado.aviso.texto, fontWeight: 700 } : undefined}>
            {nf.format(v)}
          </span>
        ),
      },
      {
        title: '%',
        key: 'pct',
        className: 'fct-num',
        align: 'right',
        render: (_: unknown, row) => `${pct(row.vinc, row.total)}%`,
      },
    ]}
    rowClassName={(row) => claseFilaAtencion(row.critico && row.pend > 0 ? 'atencion' : 'normal')}
  />
);

/** Card de métrica de un sistema (CU2 o Gescom). */
const MetricaCard: React.FC<{
  titulo: string;
  total: number;
  vinc: number;
  detalle?: DetalleFila[];
  nota?: string;
}> = ({ titulo, total, vinc, detalle, nota }) => {
  const sinv = total - vinc;
  const p = pct(vinc, total);
  return (
    <Card size="small" styles={{ body: { padding: 18 } }}>
      <p className="fct-kpi__etiqueta">{titulo}</p>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '6px 0' }}>
        <span>
          <span className="fct-mono" style={{ fontSize: 30, fontWeight: 700, color: fct.tinta['900'] }}>{nf.format(vinc)}</span>
          <span style={{ fontSize: 12, color: fct.tinta['500'], marginLeft: 6 }}>vinculados</span>
        </span>
        <span className="fct-mono" style={{ fontSize: 20, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {semaforo(p) !== 'normal' && <FctGlifoAtencion nivel={semaforo(p)} />}
          {p}%
        </span>
      </div>
      <Progress percent={p} showInfo={false} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: fct.tinta['700'] }}>
        <span>Total</span>
        <span className="fct-mono">{nf.format(total)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: fct.tinta['700'] }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><FctIcono nombre="completado" /> Vinculados</span>
        <span className="fct-mono">{nf.format(vinc)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: fct.tinta['700'] }}>
        <span style={{ color: fct.tinta['700'], display: 'inline-flex', alignItems: 'center', gap: 4 }}><FctIcono nombre="hora" /> Sin vincular</span>
        <span className="fct-mono">{nf.format(sinv)}</span>
      </div>
      {detalle && <Subtabla filas={detalle} />}
      {nota && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
          {nota}
        </Text>
      )}
    </Card>
  );
};

const VinculacionResumenTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [d, setD] = useState<PanelVinculacionResumen | null>(null);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      setD(await getPanelVinculacionResumen());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !d) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar las métricas de vinculación"
        action={<Button onClick={load}>Reintentar</Button>}
      />
    );
  }

  // --- Derivaciones (mismas que el panel Python) ---
  // Farmacias CU2
  const fCu2SinTit = d.farmaciasCU2Total - d.farmaciasCU2ConTitular;
  const fCu2SinTitVinc = d.farmaciasCU2Vinc - d.farmaciasCU2ConTitularVinc;
  const detFarmaciasCu2: DetalleFila[] = [
    {
      key: 'con-tit',
      label: 'Con titular',
      total: d.farmaciasCU2ConTitular,
      vinc: d.farmaciasCU2ConTitularVinc,
      pend: d.farmaciasCU2ConTitular - d.farmaciasCU2ConTitularVinc,
      critico: true,
    },
    {
      key: 'sin-tit',
      label: 'Sin titular',
      total: fCu2SinTit,
      vinc: fCu2SinTitVinc,
      pend: fCu2SinTit - fCu2SinTitVinc,
    },
  ];

  // Farmacéuticos CU2
  const detColegiadosCu2: DetalleFila[] = [
    {
      key: 'tit',
      label: 'Titulares',
      total: d.colegiadosCU2Titulares,
      vinc: d.colegiadosCU2TitularesVinc,
      pend: d.colegiadosCU2Titulares - d.colegiadosCU2TitularesVinc,
      critico: true,
    },
    {
      key: 'emp',
      label: 'Empleados',
      total: d.colegiadosCU2Empleados,
      vinc: d.colegiadosCU2EmpleadosVinc,
      pend: d.colegiadosCU2Empleados - d.colegiadosCU2EmpleadosVinc,
    },
  ];

  // Farmacéuticos Gescom (total y vinc derivados de la segmentación)
  const cGsTotal = d.clientesGSTitulares + d.clientesGSEmpleados + d.clientesGSSinRelacionVinc;
  const cGsVinc = d.clientesGSTitularesVinc + d.clientesGSEmpleadosVinc + d.clientesGSSinRelacionVinc;
  const detColegiadosGs: DetalleFila[] = [
    {
      key: 'tit',
      label: 'Titulares',
      total: d.clientesGSTitulares,
      vinc: d.clientesGSTitularesVinc,
      pend: d.clientesGSTitulares - d.clientesGSTitularesVinc,
      critico: true,
    },
    {
      key: 'emp',
      label: 'Empleados',
      total: d.clientesGSEmpleados,
      vinc: d.clientesGSEmpleadosVinc,
      pend: d.clientesGSEmpleados - d.clientesGSEmpleadosVinc,
    },
    {
      key: 'sinrel',
      label: 'Sin relación (vinc.)',
      total: d.clientesGSSinRelacionVinc,
      vinc: d.clientesGSSinRelacionVinc,
      pend: 0,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load}>
          Actualizar
        </Button>
      </div>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* FARMACIAS: CU2 izquierda, Gescom derecha */}
        <div>
          <Title level={4} style={{ marginBottom: 12 }}>
            Farmacias
          </Title>
          <Row gutter={[18, 18]}>
            <Col xs={24} md={12}>
              <MetricaCard
                titulo="CU2"
                total={d.farmaciasCU2Total}
                vinc={d.farmaciasCU2Vinc}
                detalle={detFarmaciasCu2}
              />
            </Col>
            <Col xs={24} md={12}>
              <MetricaCard titulo="Gescom" total={d.farmaciasGSTotal} vinc={d.farmaciasGSVinc} />
            </Col>
          </Row>
        </div>

        {/* FARMACÉUTICOS: CU2 izquierda, Gescom derecha */}
        <div>
          <Title level={4} style={{ marginBottom: 12 }}>
            Farmacéuticos
          </Title>
          <Row gutter={[18, 18]}>
            <Col xs={24} md={12}>
              <MetricaCard
                titulo="CU2"
                total={d.colegiadosCU2Total}
                vinc={d.colegiadosCU2Vinc}
                detalle={detColegiadosCu2}
              />
            </Col>
            <Col xs={24} md={12}>
              <MetricaCard
                titulo="Gescom"
                total={cGsTotal}
                vinc={cGsVinc}
                detalle={detColegiadosGs}
                nota="No se cuentan los registros dados de baja en Gescom."
              />
            </Col>
          </Row>
        </div>
      </Space>
    </div>
  );
};

export default VinculacionResumenTab;
